import React from 'react';
import { useHistory } from 'react-router-dom';

import { EAdSenseProvider } from '@domain/enums/common/EAdSense';
import { EGateway } from '@domain/enums/common/EGateway';
import { EEcommerceProvider } from '@domain/enums/common/integrations/EEcommerceProvider';
import { EStep } from '@domain/enums/onboarding/EStep';
import { IOnboardingStep } from '@domain/interfaces/onboarding/IOnboarding';
import { IEcommerceProvider } from '@domain/interfaces/onboarding/traditionalEcommerce/IEcommerce';
import { IStoreIntegrations } from '@domain/interfaces/stores/IStores';

import { useOnboarding } from '@helpers/hooks/pages/onboarding/useOnboarding';
import { useConfig } from '@helpers/hooks/useConfig';
import { useToast } from '@helpers/hooks/useToast';

import migrationService from '@services/pages/subscription/migration';

import onboardingStepService from '@services/pages/onboarding/onboardingStep';
import subscriptionService from '@services/pages/subscription/subscription';

const EcommerceContext = React.createContext<IEcommerceProvider | null>(null);

export const EcommerceProvider: React.FC = ({ children }) => {
  const { user, analytics } = useConfig();
  const { toast } = useToast();
  const history = useHistory();
  const { completeStep } = useOnboarding();

  const [selectedProvider, setSelectedProvider] = React.useState<EEcommerceProvider | undefined>(
    undefined,
  );
  const [selectedGateways, setSelectedGateways] = React.useState<Array<EGateway>>([]);
  const [selectedMarketings, setSelectedMarketings] = React.useState<Array<EAdSenseProvider>>([]);
  const [currentGatewayToIntegrate, setCurrentGatewayToIntegrate] = React.useState<
    EGateway | undefined
  >(undefined);
  const [currentMarketingToIntegrate, setCurrentMarketingToIntegrate] = React.useState<
    EAdSenseProvider | undefined
  >(undefined);
  const [hasSelectedStoreIntegrations, setHasSelectedStoreIntegrations] = React.useState<boolean>(
    false,
  );
  const [storeIntegrations, setStoreIntegrations] = React.useState<IStoreIntegrations | undefined>(
    undefined,
  );
  const [isLoadingTrial, setIsLoadingTrial] = React.useState<boolean>(false);
  const [trialDays, setTrialDays] = React.useState<number>(8);
  const [storeCase, setStoreCase] = React.useState<string>('');

  const createTrial = React.useCallback(
    async storeAliasId => {
      setIsLoadingTrial(true);

      try {
        const { data: trialData } = await subscriptionService.createTrial({ storeAliasId });

        analytics?.track(
          'Trial Started',
          {
            trial_start_date: trialData?.subscription?.period_started_at,
            trial_end_date: trialData?.subscription?.period_ended_at,
            email: user?.email,
          },
          { context: { groupId: storeAliasId } },
        );

        const dataLayer = (window as Record<string, any>)?.dataLayer;

        if (dataLayer) {
          dataLayer.push({ event: 'Trial Started' });
        }

        const {
          data: onboardingStepsData,
        } = await onboardingStepService.listOnboardingStepsPromise({ storeAliasId });

        const foundOnboardingStep:
          | IOnboardingStep
          | undefined = onboardingStepsData.onboarding_steps.find(
          (onboardingStep: IOnboardingStep) =>
            onboardingStep.configuration_step === EStep.STORE_DSERS_CONFIG, // TODO: Veificar essa parte relacionada ao dsers
        );

        if (foundOnboardingStep) {
          completeStep(foundOnboardingStep.id);
        }

        setIsLoadingTrial(false);

        history.push(`/onboarding/${storeAliasId}/finished`);
      } catch (error: any) {
        setIsLoadingTrial(false);
        toast.error(error?.response?.data?.message);
      }
    },
    [toast, history, completeStep, analytics, user],
  );

  const handleStoreBonus = React.useCallback(
    async storeAliasId => {
      setIsLoadingTrial(true);

      try {
        const { data: subscriptionData } = await subscriptionService.getSubscriptionPromise({
          storeAliasId,
        });

        if (subscriptionData.subscription) {
          history.push(`/onboarding/${storeAliasId}/finished`);

          setIsLoadingTrial(false);

          return undefined;
        }

        const { data: trialDaysData } = await migrationService.calculateTrialDaysPromise({
          storeAliasId,
        });

        setTrialDays(trialDaysData.trial_days);
        setStoreCase(trialDaysData.case);

        const APPMAX_CASE = trialDaysData.case === 'appmax_plan';
        const TRIAL_CASE =
          trialDaysData.case === 'new_user' ||
          trialDaysData.case === 'trial_in_progress' ||
          trialDaysData.case === 'canceled_more_than_30_days';
        const BONUS_CASE =
          trialDaysData.case === 'pagarme_subscription' ||
          trialDaysData.case === 'free_plan' ||
          trialDaysData.case === 'canceled_less_than_30_days' ||
          trialDaysData.case === 'old_subscription';

        if (APPMAX_CASE) {
          setIsLoadingTrial(false);
          history.push(`/onboarding/${storeAliasId}/ecommerce/bonus-appmax`);
        }

        if (BONUS_CASE) {
          setIsLoadingTrial(false);
          history.push(`/onboarding/${storeAliasId}/ecommerce/bonus`);
        }

        if (TRIAL_CASE) {
          await createTrial(storeAliasId);
        }
      } catch (error: any) {
        setIsLoadingTrial(false);
        toast.error(error?.response?.data?.message);
      }

      return undefined;
    },
    [toast, history, createTrial],
  );

  const handleSelectedProvider = React.useCallback(provider => setSelectedProvider(provider), []);

  const handleSelectedGateways = React.useCallback(gateways => setSelectedGateways(gateways), []);

  const handleSelectedMarketings = React.useCallback(
    marketings => setSelectedMarketings(marketings),
    [],
  );

  const handleStoreIntegrations = React.useCallback(
    integrations => setStoreIntegrations(integrations),
    [],
  );

  const handleSelectedStoreIntegrations = React.useCallback(
    status => setHasSelectedStoreIntegrations(status),
    [],
  );

  const handleNextGatewayToIntegrate = React.useCallback(() => {
    if (selectedGateways.length) {
      const selectedGatewaysArrayCopy = [...selectedGateways];

      const nextGatewayToIntegrate = selectedGatewaysArrayCopy.shift();

      setCurrentGatewayToIntegrate(nextGatewayToIntegrate);
      setSelectedGateways([...selectedGatewaysArrayCopy]);

      return true;
    }

    setCurrentGatewayToIntegrate(undefined);

    return false;
  }, [selectedGateways]);

  const handleNextMarketingToIntegrate = React.useCallback(() => {
    if (selectedMarketings.length) {
      const selectedMarketingsArrayCopy = [...selectedMarketings];

      const nextMarketingToIntegrate = selectedMarketingsArrayCopy.shift();

      setCurrentMarketingToIntegrate(nextMarketingToIntegrate);
      setSelectedMarketings([...selectedMarketingsArrayCopy]);

      return true;
    }

    setCurrentMarketingToIntegrate(undefined);

    return false;
  }, [selectedMarketings]);

  React.useEffect(() => {
    if (storeIntegrations) {
      const foundStoreProvider = storeIntegrations.store_front.find(provider => provider.is_active);

      if (foundStoreProvider) setSelectedProvider(foundStoreProvider.name as EEcommerceProvider);

      const foundActiveStoreGateways = storeIntegrations.gateways.filter(
        gateway => gateway.is_active,
      );

      const hasOtherGateway = storeIntegrations.gateways.find(
        gateway => gateway.other_gateway?.has_other_gateway,
      );

      const mappedActiveStoreGateways = foundActiveStoreGateways.map(
        gateway => gateway.name as EGateway,
      );

      if (hasOtherGateway) {
        const otherGatewaysNames =
          (hasOtherGateway.other_gateway?.gateway_names as Array<EGateway>) ||
          ([] as Array<EGateway>);

        const uniqueGatewaysNames = [...new Set(otherGatewaysNames)];

        mappedActiveStoreGateways.push(...uniqueGatewaysNames);
      }

      setSelectedGateways(mappedActiveStoreGateways);
    }
  }, [storeIntegrations]);

  return (
    <EcommerceContext.Provider
      value={{
        handleSelectedProvider,
        selectedProvider,
        handleStoreIntegrations,
        storeIntegrations,
        handleSelectedGateways,
        selectedGateways,
        handleSelectedMarketings,
        selectedMarketings,
        handleSelectedStoreIntegrations,
        hasSelectedStoreIntegrations,
        handleNextGatewayToIntegrate,
        currentGatewayToIntegrate,
        handleNextMarketingToIntegrate,
        currentMarketingToIntegrate,
        handleStoreBonus,
        isLoadingTrial,
        storeCase,
        trialDays,
      }}
    >
      {children}
    </EcommerceContext.Provider>
  );
};

export const useEcommerce = (): IEcommerceProvider => {
  const context = React.useContext(EcommerceContext);

  if (!context) {
    throw new Error('useEcommerce must be used within EcommerceProvider');
  }

  return context;
};
